<script lang="ts" setup>
import { computed, onBeforeUnmount, ref, shallowRef, watch } from 'vue';
import { watchDebounced } from '@vueuse/core';

import { GeolocationPersistence } from '@/core/shared/geolocation/geolocation.persistence';
import {
  MILE_IN_METRES,
  googleMapsBrowserKey,
  loadMapsLibrary,
} from '@/core/shared/google-maps/google-maps.loader';

const props = withDefaults(
  defineProps<{
    city: string | null;
    state: string | null;
    radiusMiles: number;
    apiKey?: string;
    debounceMs?: number;
  }>(),
  { apiKey: () => googleMapsBrowserKey(), debounceMs: 400 },
);

const geolocationPersistence = new GeolocationPersistence();

const place = computed(() => {
  const city = props.city?.trim();
  const state = props.state?.trim();
  return city && state ? { city, state, label: `${city}, ${state}` } : null;
});

// No key means infra has not provisioned one yet: render nothing rather than a broken map.
const isEnabled = computed(() => !!props.apiKey && place.value !== null);

type Status = 'loading' | 'ready' | 'not-found';
const status = ref<Status>('loading');
const mapEl = ref<HTMLElement | null>(null);
const map = shallowRef<google.maps.Map | null>(null);
const circle = shallowRef<google.maps.Circle | null>(null);
let latestRequest = 0;

const headerText = computed(() =>
  place.value ? `Candidates within ${props.radiusMiles} miles of ${place.value.label}` : '',
);

const radiusMetres = computed(() => props.radiusMiles * MILE_IN_METRES);

async function drawTerritory(center: google.maps.LatLngLiteral) {
  if (!mapEl.value) return;
  const { Map, Circle } = await loadMapsLibrary(props.apiKey);

  if (!map.value) {
    map.value = new Map(mapEl.value, {
      center,
      zoom: 8,
      disableDefaultUI: true,
      zoomControl: true,
      gestureHandling: 'cooperative',
    });
  }

  if (!circle.value) {
    circle.value = new Circle({
      map: map.value,
      center,
      radius: radiusMetres.value,
      strokeColor: '#6C40E5',
      strokeWeight: 2,
      fillColor: '#6C40E5',
      fillOpacity: 0.08,
    });
  } else {
    circle.value.setCenter(center);
    circle.value.setRadius(radiusMetres.value);
  }

  fitToCircle();
}

function fitToCircle() {
  const bounds = circle.value?.getBounds();
  if (bounds) {
    map.value?.fitBounds(bounds);
  }
}

async function locate() {
  if (!isEnabled.value || !place.value) return;
  const request = ++latestRequest;
  status.value = 'loading';

  const resolved = await geolocationPersistence.geocodeCityState(
    place.value.city,
    place.value.state,
  );
  if (request !== latestRequest) return; // a newer city/state won

  if (!resolved) {
    status.value = 'not-found';
    return;
  }

  status.value = 'ready';
  await drawTerritory(resolved.coordinates);
}

watchDebounced(() => [props.city, props.state, props.apiKey], locate, {
  debounce: props.debounceMs,
  immediate: true,
});

// A radius change only resizes the circle; the city has not moved.
watch(radiusMetres, (radius) => {
  if (!circle.value) return;
  circle.value.setRadius(radius);
  fitToCircle();
});

// The card unmounts while City is empty; a map bound to the old element would draw nowhere.
watch(mapEl, () => {
  circle.value?.setMap(null);
  circle.value = null;
  map.value = null;
});

onBeforeUnmount(() => {
  circle.value?.setMap(null);
});
</script>

<template>
  <div
    v-if="isEnabled"
    data-test="territory-check-card"
    class="overflow-hidden rounded-xl border border-tint-80 bg-white font-sans"
  >
    <div class="flex flex-wrap items-baseline gap-2 border-b border-tint-80 px-6 py-4">
      <span class="text-[15px] font-bold text-shade-880">Sourcing area</span>
      <span data-test="territory-check-header" class="text-sm text-shade-800">{{
        headerText
      }}</span>
    </div>

    <div class="relative h-60 bg-tint-20">
      <div
        v-show="status === 'ready'"
        ref="mapEl"
        data-test="territory-check-map"
        class="h-full w-full"
      />
      <p
        v-if="status === 'loading'"
        class="absolute inset-0 flex items-center justify-center text-sm text-shade-800"
      >
        Placing {{ place?.label }} on the map…
      </p>
      <p
        v-if="status === 'not-found'"
        data-test="territory-check-error"
        class="absolute inset-0 flex items-center justify-center px-6 text-center text-sm text-shade-880"
      >
        We couldn't place {{ place?.label }} on the map — check the city.
      </p>
    </div>

    <p class="border-t border-tint-80 px-6 pb-5 pt-4 text-[13px] leading-[1.5] text-shade-800">
      Your AI recruiter will look for candidates who live in this area. If it looks wrong, update
      the city or distance before activating the job.
    </p>
  </div>
</template>
